import Plyr from 'plyr';
import { Record } from 'notadb';
import { bem } from '~/common/classname';
import type { RecordType } from '~/highlight/types/record';
import './styles.scss';
import PlayList from '../PlayList';

type Props = {
  list: RecordType[];
  selectedRecord: RecordType | null;
};

type StateType = {
  player?: Plyr;
  selectedRecord?: RecordType | null;
  [key: string]: any;
};

class Player {
  props: Props;
  state: StateType;
  isAutoChange: boolean;
  orientation: 'horizontal' | 'vertical';
  onPlaylistClick: (id: number) => void;
  onCurrentTimeUpdate: (event: Plyr.PlyrEvent) => void;

  constructor(props: Props) {
    this.props = { ...props };
    const { selectedRecord } = this.props;
    this.state = { selectedRecord };
    this.isAutoChange = false;
    this.orientation = 'horizontal';
    this.onPlaylistClick = this.handlePlaylistClick.bind(this);
    this.onCurrentTimeUpdate = this.handleCurrentTimeUpdate.bind(this);

    this.mounted();
  }

  mounted() {
    const { selectedRecord } = this.state;
    const { thumbnail } = selectedRecord;
    if (!thumbnail) return;

    const img = new Image();
    img.onload = () => {
      this.orientation = img.width >= img.height ? 'horizontal' : 'vertical';

      const classname = bem(['bai-highlight-modal', 'main', 'video-wrapper']);
      const videoWrapper = document.querySelector(`.${classname}`);
      if (!videoWrapper) return;

      videoWrapper.classList.remove(
        `${bem([classname, 'horizontal'], true)}`,
        `${bem([classname, 'vertical'], true)}`
      );
      videoWrapper.classList.add(`${bem([classname, this.orientation], true)}`);
    };
    img.src = thumbnail;
  }

  setState(newState: StateType) {
    this.state = { ...this.state, ...newState };
    if (newState.selectedRecord && !this.isAutoChange) {
      const { properties } = newState.selectedRecord;
      const marker = properties['marker']?.value ?? [];
      this.moveCurrentTime(marker[0].value ?? 0);
    }
    this.renderKeywords();
  }

  moveCurrentTime(value: number) {
    this.state.player.currentTime = value;
  }

  template() {
    const { thumbnail } = this.state.selectedRecord ?? {};
    const classname = bem(['bai-highlight-modal', 'main', 'video-wrapper']);
    return /*html*/ `
      <div class="${classname} ${bem([classname, this.orientation], true)}">
        <video playsinline preload="metadata" data-poster="${thumbnail}"></video>
      </div>
    `;
  }

  async setupM3u8(videoElement: HTMLVideoElement, player: Plyr) {
    const { selectedRecord } = this.state;
    const { media } = selectedRecord;
    const { properties } = selectedRecord;
    const marker = properties['marker']?.value ?? [];
    const starttime = marker[0]?.value ?? 0;
    const Hls = (await import('hls.js')).default;

    if (Hls.isSupported()) {
      const hls = new Hls({
        startPosition: starttime,
        maxBufferSize: 30 * 1000 * 1000,
      });
      hls.loadSource(media[0].url);
      hls.attachMedia(videoElement);

      // error handle
      hls.on(Hls.Events.ERROR, function (event, data) {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR: {
              console.log(
                'HLS_MEDIA_ERROR: fatal media error encountered, try to recover'
              );
              hls.recoverMediaError();
              break;
            }
            case Hls.ErrorTypes.NETWORK_ERROR: {
              console.error('HLS ERROR: fatal network error encountered', data);
              break;
            }
            default: {
              // cannot recover
              console.log('HLS ERROR: unavailable to recover');
              hls.destroy();
              break;
            }
          }
        }
      });
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      // ios/safari
      this.setupPlyr(videoElement);
    }
  }

  setupPlyr(videoElement: HTMLVideoElement) {
    const { selectedRecord } = this.state;
    if (!selectedRecord) return;

    const { media, properties } = selectedRecord;
    const marker = properties['marker']?.value ?? [];
    const starttime = marker[0]?.value ?? 0;

    videoElement.src = media[0].url;
    videoElement.addEventListener('loadedmetadata', () => {
      videoElement.currentTime = starttime;
      if (this.state.player) {
        this.state.player.currentTime = starttime;
      }
    });
  }

  setup() {
    const { media } = this.state.selectedRecord ?? {};
    if (!media?.[0]?.url) return;

    const src = new URL(media[0].url);
    const ext = src.pathname.split('.').pop();
    if (!ext) {
      console.error('ERROR: no extension');
      return;
    }

    const video = document.querySelector<HTMLVideoElement>(
      `.${bem(['bai-highlight-modal', 'main', 'video-wrapper'])} > video`
    );
    if (!video) {
      console.error('ERROR: No video element');
      return;
    }

    const markers = this.props.list.map((record) => {
      const { properties } = record;
      const marker = properties['marker']?.value ?? [];
      const starttime = marker[0]?.value ?? 0;
      return { label: '', time: starttime };
    });
    const player = new Plyr(video, {
      controls: [
        'play-large',
        'play',
        'progress',
        'current-time',
        'mute',
        'fullscreen',
      ],
      invertTime: false,
      markers: {
        enabled: true,
        points: markers,
      },
      hideControls: false,
    });
    player.on('timeupdate', this.onCurrentTimeUpdate);

    if (ext === 'mp4') {
      this.setupPlyr(video);
    } else if (ext === 'm3u8') {
      this.setupM3u8(video, player);
    }
    this.setState({ player });
  }

  renderKeywords() {
    const controller = document.querySelector<HTMLElement>('.plyr__controls');
    if (!controller) {
      console.error('no plyr controls');
      return;
    }

    const { list } = this.props;
    const { selectedRecord } = this.state;
    new PlayList({
      target: controller,
      list,
      selectedRecord,
      onClick: this.onPlaylistClick,
    });
  }

  handlePlaylistClick(id: number) {
    const selected = this.props.list.find((record) => record.id === id);
    if (selected) {
      this.isAutoChange = false;
      this.setState({ selectedRecord: selected });
    }
  }

  handleCurrentTimeUpdate(event: Plyr.PlyrEvent) {
    const { currentTime } = event.detail.plyr;
    const selectedRecord = this.props.list
      .filter((record) => {
        const [start, end] = new Record(record).prop('marker') ?? [];
        return (
          currentTime >= (start.value ?? 0) && currentTime <= (end.value ?? 0)
        );
      })
      .pop();

    if (!selectedRecord) {
      this.setState({ selectedRecord: null });
      return;
    }

    if (this.state.selectedRecord?.id === selectedRecord.id) return;

    if (!this.isAutoChange) {
      this.isAutoChange = true;
    } else {
      this.setState({ selectedRecord });
    }
  }
}

export default Player;
